<script>
import SaleStatusBadge from "@src/components/ui-design/badges/SaleStatusBadge.vue";
import SalesService from "@services/resources/SalesService";
import { getSaleRecurrence } from "@src/helpers/sale";
import { getPaymentMethod } from "@src/helpers/payment";
import Money from "@src/mixins/money";
import ChargesModal from "./ChargesModal.vue";
import ChargeRefundAction from "./ChargeRefundAction.vue";
import RefundInstallmentModal from "../components/modals/RefundInstallmentModal.vue";
import {
  getRefundRequestErrorMessage,
  REFUND_SUBJECT_CHARGE,
} from "../refundEligibility";

const service = SalesService.build();

export default {
  emits: ["reload"],
  mixins: [Money],
  components: {
    SaleStatusBadge,
    ChargesModal,
    ChargeRefundAction,
    RefundInstallmentModal,
  },
  props: {
    sale: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      baseFields: [
        { key: "id", label: "ID" },
        { key: "created_at", label: "Data" },
        { key: "amount", label: "Valor" },
        { key: "status", label: "Status" },
        { key: "method", label: "Método" },
      ],
      isOpen: false,
      selectedRefundCharge: {},
      refundLoading: false,
      refundErrorMessage: "",
    };
  },
  computed: {
    isSubscription() {
      return this.sale.type === "SUBSCRIPTION";
    },
    fields() {
      if (!this.isSubscription) return this.baseFields;

      return [
        ...this.baseFields,
        {
          key: "action",
          label: "Ação",
          thClass: "sales-details__action-header",
          tdClass: "sales-details__action-cell",
        },
      ];
    },
    contractPeriod() {
      const contract = this.sale && this.sale.client_contract && this.sale.client_contract.contract;
      return contract ? contract.period : null;
    },
  },
  methods: {
    getSaleRecurrence,
    getPaymentMethod,
    showRefundInstallmentModal(charge) {
      this.selectedRefundCharge = charge;
      this.$bvModal.show("refund-installment-modal");
    },
    async refundInstallment(charge) {
      if (this.refundLoading) return;

      this.refundLoading = true;
      this.refundErrorMessage = "";

      try {
        await service.refund(charge.id, "REEMBOLSAR PARCELA");

        this.$bvToast.toast("Solicitação de reembolso iniciada.", {
          title: "Solicitação enviada!",
          variant: "success",
          autoHideDelay: 5000,
          appendToast: true,
        });

        this.$bvModal.hide("refund-installment-modal");
        this.$bvModal.hide("charges-modal");
        this.$emit("reload");
      } catch (error) {
        this.refundErrorMessage = getRefundRequestErrorMessage(
          error,
          "Não foi possível iniciar o reembolso. Tente novamente.",
          REFUND_SUBJECT_CHARGE
        );
      } finally {
        this.refundLoading = false;
      }
    },
    clearRefundError() {
      this.refundErrorMessage = "";
    },
    resetChargesModal() {
      this.isOpen = false;
      this.selectedRefundCharge = {};
    },
    handleModal() {
      if (!this.sale.client_contract) return;

      this.isOpen = true;

      this.$nextTick(() => {
        this.$bvModal.show('charges-modal')
      })
    }
  },
};
</script>

<template>
  <div class="card-box p-3 px-0">
    <ChargesModal
      v-if="isOpen && sale.client_contract"
      @refund="showRefundInstallmentModal"
      @hidden="resetChargesModal"
      :show-refund-action="isSubscription"
      :subscription-info="{
        period: getSaleRecurrence(contractPeriod),
        charges: sale.client_contract.paid_charges_count,
        total: formatMoney(sale.client_contract.paid_charges_amount),
      }"
    />
    <RefundInstallmentModal
      v-if="isSubscription"
      :charge="selectedRefundCharge"
      :sale="sale"
      :loading="refundLoading"
      :error-message="refundErrorMessage"
      @confirm="refundInstallment"
      @hidden="clearRefundError"
    />

    <div class="d-flex align-items-center justify-content-between mb-2">
      <div>
        <h6 class="sales-details__subtitle">Histórico de Cobranças</h6>
        <p>Últimas cobranças relacionadas a esta venda</p>
      </div>
      <b-button v-if="sale.client_contract" class="sales-details__button font-weight-bold" variant="outline-primary"
        @click="handleModal">
        Ver todas
      </b-button>
    </div>

    <template v-if="sale.client_contract">
      <b-table class="sales-details__table" :items="sale.client_contract.charges" :fields="fields" headless bordered small>
        <template #cell(id)="data">
          <span>#{{ data.item.id }}</span>
        </template>
        <template #cell(created_at)="data">
          <span>{{ $date(data.item.created_at) }}</span>
        </template>

        <template #cell(amount)="data">
          <div class="d-flex justify-content-start">
            R$ {{ formatMoney(data.item.amount) }}
          </div>
        </template>
        <template #cell(status)="data">
          <div class="d-flex justify-content-start">
            <SaleStatusBadge :status="data.item.status" />
          </div>
        </template>
        <template #cell(method)="data">
          <div class="d-flex justify-content-start">
            {{ getPaymentMethod(data.item.method) }}
          </div>
        </template>
        <template #cell(action)="data">
          <ChargeRefundAction
            :charge="data.item"
            @refund="showRefundInstallmentModal"
          />
        </template>
      </b-table>
      <div class="sales-next-charges__bottom">
        <div>
          <p>Período</p>
          <p class="sales-next-charges__text">{{ getSaleRecurrence(contractPeriod) }}</p>
        </div>
        <div>
          <p>Cobranças Pagas</p>
          <p class="sales-next-charges__text">{{ sale.client_contract.paid_charges_count }}</p>
        </div>
        <div>
          <p>Valor Total Pago</p>
          <p class="sales-next-charges__text">R$ {{ formatMoney(sale.client_contract.paid_charges_amount) }}</p>
        </div>
      </div>
    </template>
    <div v-else class="text-center text-muted py-4">
      Informações de cobrança não disponíveis.
    </div>
  </div>
</template>

<style lang="scss">
.sales-details__action-header,
.sales-details__action-cell {
  text-align: center;

  .sales-button {
    justify-content: center;
  }
}

.sales-next-charges {
  &__bottom {
    display: flex;
    justify-content: space-between;
    background: #f7f7f7;
    padding: 8px 20px;
  }

  &__text {
    color: #000c0b;
    font-weight: 600;
  }
}
</style>
